<template>
    <div
        class="section-card ppv2-rounded-[10px] ppv2-border ppv2-border-black ppv2-border-opacity-20 ppv2-overflow-visible"
        :class="{ 'ppv2-bg-white dark:ppv2-bg-dark': !customBg, customBg }"
    >
        <div
            class="md:ppv2-p-5 ppv2-p-3 ppv2-flex ppv2-items-center ppv2-justify-between ppv2-gap-3"
            :class="{ 'ppv2-cursor-pointer': (collapsible || !hideToggle) && !disabled, 'ppv2-opacity-50': disabled }"
            :style="{ minHeight: normalizeSizeVertical(85) }"
            @click="handleHeaderClick"
        >
            <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center ppv2-gap-2">
                <slot name="header-left"></slot>
                <h3 v-if="title" class="poppinsSemiBold ppv2-text-black ppv2-m-0" :style="{ fontSize: normalizeFontSize(20) }">
                    {{ title }}
                </h3>
                <div v-if="tooltip" class="ppv2-group ppv2-relative" @click.stop>
                    <Info :size="16" class="ppv2-text-[#5076E1]" />
                    <div
                        class="ppv2-absolute ppv2-top-1/2 ppv2--translate-y-1/2 ppv2-left-[25px] ppv2-px-3 ppv2-py-2 ppv2-bg-[#171647] ppv2-text-white ppv2-text-xs ppv2-rounded-lg ppv2-opacity-0 group-hover:ppv2-opacity-100 ppv2-whitespace-nowrap ppv2-transition-opacity ppv2-z-10"
                    >
                        <!-- Triangle -->
                        <div
                            class="ppv2-absolute ppv2-right-full ppv2-top-1/2 ppv2--translate-y-1/2 ppv2-border-y-[6px] ppv2-border-y-transparent ppv2-border-r-[6px] ppv2-border-r-[#171647]"
                        ></div>
                        {{ tooltip }}
                    </div>
                </div>
                <div v-if="hasSubcriptionEssentiel" class="ppv2-mb-2">
                    <span
                        class="ppv2-bg-[#7D2DBD] ppv2-text-white ppv2-px-3 ppv2-py-1 ppv2-rounded-full ppv2-text-[10px] poppinsSemiBold ppv2-flex ppv2-items-center ppv2-gap-1 ppv2-w-fit"
                    >
                        {{ $t('subscription.plan_required.essentiel') }}
                    </span>
                </div>
                <slot name="header-right"></slot>
            </div>
            <div class="ppv2-flex ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                <slot name="header-actions">
                    <div v-if="!hideToggle && !collapsible" @click.stop="toggle">
                        <BaseSwitch
                            customLabelStyle="poppinsLight"
                            :label="showSwitchLabel ? (localValue ? 'Activé' : 'Désactivé') : ''"
                            :modelValue="localValue"
                            :disabled="disabled || hasSubcriptionEssentiel"
                            size="md"
                        />
                    </div>
                </slot>
            </div>
            <div v-if="collapsible && !disabled" class="ppv2-ml-4">
                <ChevronDown
                    :size="20"
                    class="ppv2-transition-transform ppv2-duration-200"
                    :class="{ 'ppv2-rotate-180': internalExpanded }"
                />
            </div>
        </div>

        <div v-if="disabled" class="ppv2-px-5 ppv2-pb-5">
            <slot name="warning"></slot>
        </div>

        <transition name="fade">
            <div
                v-show="shouldShowContent && !disabled"
                class="ppv2-border-t ppv2-border-black ppv2-border-opacity-20"
                :style="{
                    padding: normalizeSize(20, true),
                }"
            >
                <slot></slot>
            </div>
        </transition>
    </div>
</template>

<script>
import BaseSwitch from '../../../../Shared/BaseSwitch.vue'
import { Info, ChevronDown } from 'lucide-vue'
import normalizeSize from '../../../../../utils/normalizeSize'
import normalizeSizeVertical from '../../../../../utils/normalizeSizeVertical'
import normalizeFontSize from '../../../../../utils/normalizeFontSize'

export default {
    name: 'SectionCard',
    components: {
        BaseSwitch,
        Info,
        ChevronDown,
    },
    props: {
        value: { type: Boolean, default: false },
        title: { type: String, required: true },
        tooltip: { type: String, default: '' },
        hideToggle: { type: Boolean, default: false },
        collapsible: { type: Boolean, default: false },
        initiallyExpanded: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        hasSubcriptionEssentiel: { type: Boolean, default: false },
        showSwitchLabel: { type: Boolean, default: true },
        customBg: { type: String, default: '' },
    },
    data() {
        return {
            localValue: this.value,
            internalExpanded: this.initiallyExpanded,
        }
    },
    computed: {
        shouldShowContent() {
            if (this.disabled) return false
            if (this.collapsible) return this.internalExpanded
            return this.hideToggle || this.localValue
        },
    },
    watch: {
        value(newVal) {
            this.localValue = newVal
        },
        initiallyExpanded(newVal) {
            this.internalExpanded = newVal
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        normalizeSizeVertical,
        toggle() {
            if (this.disabled) return
            // Update local value immediately for instant visual feedback
            this.localValue = !this.localValue

            // Defer the parent state update to allow the toggle UI to update first
            setTimeout(() => {
                this.$emit('input', this.localValue)
            }, 50)
        },
        handleHeaderClick() {
            if (this.disabled) return
            if (this.collapsible) {
                this.internalExpanded = !this.internalExpanded
                this.$emit('toggle', this.internalExpanded)
            } else if (!this.hideToggle) {
                this.toggle()
            }
        },
    },
}
</script>

<style scoped>
.section-card {
    border: 1px solid #e2e8f0;
    transition: box-shadow 0.2s ease-in-out;
}
.dark .section-card {
    border-color: #334155;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.15s ease-in-out;
}
.fade-enter,
.fade-leave-to {
    opacity: 0;
}
</style>
